/**
 * Check if the credential is configured correctly.
 * @param {object} credential Credential
 * @returns {boolean} True if the credential is fully configured.
 */
export function isConfigurationValid(credential) {
	if (credential && credential.status) {
		return [
			'needsBasic',
			'needsAPIKey',
			'needsClient'
		].indexOf(credential.status.action) === -1;
	}
	return true;
}

/**
 * Check if the credential is in an authorized state.
 * @param {object} credential Credential
 * @returns {boolean} True if the credential is authorized.
 */
export function isAuthorized(credential) {
	return !!credential
		&& isConfigurationValid(credential)
		&& credential.status
		&& credential.status.action === 'none'
		&& credential.status.credential === 'ok';
}

/**
 * Check if the credential can potentially be authorized.
 * @param {object} credential Credential
 * @returns {boolean} True if the credential is authorized.
 */
export function canAuthorize(credential) {
	// For now only OAuth2 credentials that are validly configured and have
	// a refresh token or an authentication url are authorizable.
	return !!credential
		&& isConfigurationValid(credential)
		&& credential.type === 'oauth2'
		&& !!credential.authentication_url
		&& !!credential.formatted_authentication_url;
}

/**
 * Compare two credentials to see if they have the same token values.
 * @param {object} cred Credential.
 * @param {object} other Credential.
 * @returns {boolean} True if the credential tokens are the same.
 */
export function isSameToken(cred, other) {
	if (cred === other) {
		return true;
	} else if (cred.type !== other.type) {
		return false;
	} else if (cred.type === 'oauth2') {
		return (cred.refresh_token === other.refresh_token)
			&& (cred.access_token === other.access_token);
	} else if (cred.type === 'apiKey') {
		return (cred.key === other.key);
	} else if (cred.type === 'basic') {
		return (cred.username === other.username) && (cred.password === other.password);
	} else {
		return false;
	}
}

/**
 * Test if a value is a datauri encoded image.
 * @param {string} value The value to test.
 * @returns {boolean} True if the value looks like an dataURI.
 */
export function isImageDataURI(value) {
	return !!(value
		&& typeof value === 'string'
		&& value.match(/^data:image\/(?:bmp|jpeg|png|gif|tiff|svg\+xml);base64,.*/));
}
